import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt, faCheck, faSave, faClock,
  faExclamationCircle, faBan, faCalendarAlt,
  faCalendar, faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

const DashboardRentas = () => {
  const [resumen, setResumen] = useState({
    total: 0,
    pendiente: 0,
    declarada: 0,
    guardada: 0,
    diferida: 0,
    observada: 0,
    noHacer: 0,

    pagoPendiente: 0  // Añadir contador para el nuevo estado
  });
  
  const [prorroga] = useState('30.04.2025');

  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();
  const [resumenAsignados, setResumenAsignados] = useState([]);
  const { currentUser } = useContext(UserContext);
  const isAdmin = currentUser?.role === 'ADMIN';

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };


  const daysUntil = (dateString) => {
    try {
      const parts = dateString.split('.');
      if (parts.length === 3) {
        // Convertir formato DD.MM.YYYY a Date
        const target = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        if (isNaN(target)) return '-';
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const diffMs = target.getTime() - today.getTime();
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      }
      return '-';
    } catch (error) {
      return '-';
    }
  };


  useEffect(() => {
    const fetchResumen = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/rentas/resumen?año=${year}`,
          getAuthHeader()
        );
        setResumen(response.data);

        // Obtener resumen por asignado
        const resumenAsignadosResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/rentas/resumen-asignados?año=${year}`,
          getAuthHeader()
        );
        setResumenAsignados(resumenAsignadosResponse.data || []);

      } catch (error) {
        console.error('Error al obtener resumen de rentas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResumen();
  }, [year]);


  const cards = [
    {
      title: 'Total Rentas',
      value: resumen.total,
      icon: faFileAlt,
      color: 'bg-blue-100',
      iconColor: 'text-blue-500',
      estado: ''
    },
    {
      title: 'Pendientes',
      value: resumen.pendiente,
      icon: faClock,
      color: 'bg-gray-100',
      iconColor: 'text-gray-500',
      estado: 'pendiente'
    },
    {
      title: 'Declaradas',
      value: resumen.declarada,
      icon: faCheck,
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      estado: 'declarada'
    },
    {
      title: 'Guardadas',
      value: resumen.guardada,
      icon: faSave,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      estado: 'guardada'
    },
    {
      title: 'Diferidas',
      value: resumen.diferida,
      icon: faClock,
      color: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      estado: 'diferida'
    },
    {
      title: 'Observadas',
      value: resumen.observada,
      icon: faExclamationCircle,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600',
      estado: 'observada'
    },
    {
      title: 'No Hacer',
      value: resumen.noHacer,
      icon: faBan,
      color: 'bg-red-100',
      iconColor: 'text-red-600',
      estado: 'No hacer'
    },
    
    {
      title: 'Pago Pendiente',
      value: resumen.pagoPendiente,
      icon: faQuestionCircle,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      estado: 'pagoPendiente'
    }
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-semibold mb-6 text-center">Dashboard Rentas</h1>

      {/* Selector de año */}
      <div className="mb-6 flex justify-center">
        <label className="mr-2 text-gray-700 font-medium">Año:</label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      
      {/* Tarjetas */}
      {loading ? (
        <div className="text-center py-4">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cards.map((card, index) => (
            <div
              key={index}

              className={`rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-transform transform hover:scale-105 ${card.color}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  {/* Título y valor */}
                  <h2 className="text-base font-semibold text-gray-700">{card.title}</h2>
                  <p className="text-2xl font-bold mt-1">{card.value}</p>

                  {/* Mostrar días restantes solamente en la tarjeta de Fecha Vencimiento */}
                  {card.title === 'Fecha Vencimiento' && (
                  <p className="text-sm mt-1 text-gray-600">
                    Quedan {daysUntil(card.value)} días
                  </p>
                )}
              </div>
                {/* Icono */}
                <div className={`p-2 rounded-full ${card.color}`}>
                  <FontAwesomeIcon icon={card.icon} className={`text-3xl ${card.iconColor}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resumen por Asignado */}
      {!loading && resumenAsignados.length > 0 && (
        <div className="mt-10 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4 text-center text-gray-800">Resumen por Asignado</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead className="bg-gray-100 border-b border-gray-200 text-gray-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">Asignado</th>
                  <th className="py-3 px-4 text-center font-semibold">Total</th>
                  <th className="py-3 px-4 text-center font-semibold">Pendientes</th>
                  <th className="py-3 px-4 text-center font-semibold">Declaradas</th>
                  <th className="py-3 px-4 text-center font-semibold">Guardadas</th>
                  <th className="py-3 px-4 text-center font-semibold">Diferidas</th>
                  <th className="py-3 px-4 text-center font-semibold">Observadas</th>
                  <th className="py-3 px-4 text-center font-semibold">No Hacer</th>
                  <th className="py-3 px-4 text-center font-semibold">Pago Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {resumenAsignados.map((item) => (
                  <tr
                    key={item.asignado || 'no-asignado'}
                    className="border-t hover:bg-blue-50 cursor-pointer transition-colors"

                  >
                    <td className="py-3 px-4 font-medium text-blue-600">{item.asignado || 'No asignado'}</td>
                    <td className="py-3 px-4 text-center">{item.total}</td>
                    <td className="py-3 px-4 text-center">{item.pendiente}</td>
                    <td className="py-3 px-4 text-center text-green-600 font-semibold">{item.declarada}</td>
                    <td className="py-3 px-4 text-center text-blue-600 font-semibold">{item.guardada}</td>
                    <td className="py-3 px-4 text-center text-yellow-600 font-semibold">{item.diferida}</td>
                    <td className="py-3 px-4 text-center text-orange-600 font-semibold">{item.observada}</td>
                    <td className="py-3 px-4 text-center text-red-600 font-semibold">{item.noHacer}</td>
                    <td className="py-3 px-4 text-center text-purple-600 font-semibold">{item.pagoPendiente}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardRentas;